'use client';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import _extends from "@babel/runtime/helpers/esm/extends";
const _excluded = ["slots", "slotProps", "className", "classes", "sx", "messageId"];
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useMessage } from '@mui/x-chat-headless';
import { MessageRoot } from '@mui/x-chat-headless';
import { styled, createUseThemeProps } from "../internals/zero-styled/index.mjs";
import { useChatMessageUtilityClasses } from "./chatMessageClasses.mjs";
import { jsx as _jsx } from "react/jsx-runtime";
const useThemeProps = createUseThemeProps('MuiChatMessage');
const ChatMessageStyled = styled('div', {
  name: 'MuiChatMessage',
  slot: 'Root',
  overridesResolver: (props, styles) => [styles.root, props.ownerState?.role === 'user' && styles.roleUser, props.ownerState?.role === 'assistant' && styles.roleAssistant]
})(({
  theme,
  ownerState
}) => {
  const isCompact = ownerState?.variant === 'compact';
  const isUser = ownerState?.role === 'user';
  const densityPaddingBlock = {
    compact: theme.spacing(0.25),
    standard: theme.spacing(1),
    comfortable: theme.spacing(2)
  };
  const paddingBottom = densityPaddingBlock[ownerState?.density ?? 'standard'];
  if (isCompact) {
    // Compact: avatar and author name share the first row, content below.
    // For grouped messages (not first in group) the avatar column stays for alignment
    // but no avatar/author name is rendered.
    const isGrouped = ownerState?.isGrouped;
    return _extends({
      display: 'grid',
      columnGap: theme.spacing(1),
      width: '100%',
      boxSizing: 'border-box',
      paddingInline: theme.spacing(2),
      paddingBlock: `0 ${paddingBottom}`,
      fontFamily: theme.typography.fontFamily
    }, isGrouped ? {
      // Grouped: no avatar/authorName rows — content + meta on the same row.
      // Meta (✓ 10:55) sits top-right, aligned to the start of the content.
      gridTemplateColumns: 'var(--MuiChatMessage-avatarSize) 1fr auto',
      gridTemplateAreas: '". content meta"'
    } : {
      // First in group: avatar spans authorName + content rows.
      // Meta (✓ 10:55) sits top-right on the same row as the author name.
      gridTemplateColumns: 'var(--MuiChatMessage-avatarSize) 1fr auto',
      gridTemplateRows: 'auto auto',
      gridTemplateAreas: '"avatar authorName meta" "avatar content ."'
    });
  }
  const isGrouped = ownerState?.isGrouped;

  // Default variant: two-row grid. First row holds avatar + content bubble,
  // second row holds message actions below the bubble, aligned to the avatar side.
  // Meta is rendered inline inside the bubble via ChatMessageInlineMeta.
  return _extends({
    display: 'grid',
    gridTemplateColumns: isGrouped ? 'var(--MuiChatMessage-avatarSize) 1fr' : 'auto 1fr',
    gridTemplateRows: 'auto auto',
    gridTemplateAreas: isGrouped ? '". content" ". actions"' : '"avatar content" ". actions"',
    columnGap: theme.spacing(0.5),
    width: '100%',
    boxSizing: 'border-box',
    paddingInline: theme.spacing(2),
    // Phantom column: reserve the same width as the avatar on the opposite side so
    // assistant and user bubbles always share the same horizontal content lane.
    paddingInlineEnd: `calc(${theme.spacing(2)} + var(--MuiChatMessage-avatarSize))`,
    paddingBlock: `0 ${paddingBottom}`,
    // When actions are present, their row already provides visual spacing,
    // so reduce the bottom padding to avoid double spacing between messages.
    '&:has(.MuiChatMessage-actions)': {
      paddingBlockEnd: theme.spacing(0.25)
    },
    fontFamily: theme.typography.fontFamily
  }, isUser && {
    gridTemplateColumns: isGrouped ? '1fr var(--MuiChatMessage-avatarSize)' : '1fr auto',
    gridTemplateAreas: isGrouped ? '"content ." "actions ."' : '"content avatar" "actions ."',
    paddingInlineStart: `calc(${theme.spacing(2)} + var(--MuiChatMessage-avatarSize))`,
    paddingInlineEnd: theme.spacing(2)
  });
});
const ChatMessage = /*#__PURE__*/React.forwardRef(function ChatMessage(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiChatMessage'
  });
  const {
      slots,
      slotProps,
      className,
      classes: classesProp,
      sx,
      messageId
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const classes = useChatMessageUtilityClasses(classesProp);
  const message = useMessage(messageId);
  const stateClasses = clsx({
    'user': classes.roleUser,
    'assistant': classes.roleAssistant
  }[message?.role], {
    'streaming': classes.streaming,
    'error': classes.error
  }[message?.status]);
  return /*#__PURE__*/_jsx(MessageRoot, _extends({
    ref: ref,
    messageId: messageId
  }, other, {
    slots: _extends({
      root: slots?.root ?? ChatMessageStyled
    }, slots),
    slotProps: _extends({}, slotProps, {
      root: _extends({
        className: clsx(classes.root, stateClasses, className),
        sx
      }, slotProps?.root)
    })
  }));
});
if (process.env.NODE_ENV !== "production") ChatMessage.displayName = "ChatMessage";
process.env.NODE_ENV !== "production" ? ChatMessage.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  classes: PropTypes.object,
  className: PropTypes.string,
  isGrouped: PropTypes.bool,
  messageId: PropTypes.string.isRequired,
  slotProps: PropTypes.object,
  slots: PropTypes.object,
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object])
} : void 0;
export { ChatMessage };